import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { LayoutGrid, Brain, Watch, Compass, ArrowRight, Sparkles, Gauge, Zap, Lightbulb } from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ToolVisibility {
  barometre: boolean;
  projectorWatch: boolean;
  align: boolean;
}

const DEFAULT_VISIBILITY: ToolVisibility = {
  barometre: true,
  projectorWatch: false,
  align: false
};

export default function ToolsPage() {
  const [toolVisibility, setToolVisibility] = useState<ToolVisibility>(DEFAULT_VISIBILITY);

  useEffect(() => {
    const loadToolVisibility = async () => {
      try {
        const { data: settings, error } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'tool_visibility')
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading tool visibility:', error);
          return;
        }

        if (settings?.value) {
          setToolVisibility(settings.value as ToolVisibility);
        }
      } catch (error) {
        console.error('Error loading tool visibility:', error);
      }
    };

    loadToolVisibility();

    // Subscribe to real-time updates
    const subscription = supabase
      .channel('tool_visibility_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'admin_settings', filter: 'key=eq.tool_visibility' },
        () => {
          loadToolVisibility();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const tools = [
    {
      title: "Baromètre HED",
      description: "Évaluez votre état énergétique et recevez des recommandations personnalisées",
      icon: <Brain className="w-8 h-8 text-purple-600" />,
      link: "/barometre",
      color: "bg-purple-100",
      visible: toolVisibility.barometre
    },
    {
      title: "Projector Watch",
      description: "Suivez vos cycles énergétiques et physiologiques en temps réel",
      icon: <Watch className="w-8 h-8 text-blue-600" />,
      link: "/projector-watch",
      color: "bg-blue-100",
      visible: toolVisibility.projectorWatch
    },
    {
      title: "Align",
      description: "Programme d'alignement énergétique pour optimiser votre potentiel",
      icon: <Compass className="w-8 h-8 text-green-600" />,
      link: "/align",
      color: "bg-green-100",
      visible: toolVisibility.align
    }
  ];

  const upcomingTools = [
    {
      title: "Energy Tracker",
      description: "Application mobile pour suivre votre énergie en déplacement et recevoir des notifications personnalisées.",
      icon: <Zap className="w-8 h-8 text-amber-600" />,
      status: "En développement"
    },
    {
      title: "HD Insights",
      description: "Analyse approfondie de votre design avec visualisation interactive de votre carte et explications détaillées.",
      icon: <Lightbulb className="w-8 h-8 text-indigo-600" />,
      status: "Planifié"
    }
  ];

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-6xl mx-auto space-y-8">
        <BackButton to="/" />
        
        <div className="text-center space-y-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block p-3 bg-white rounded-2xl shadow-xl mb-4"
          >
            <LayoutGrid className="w-10 h-10 text-purple-600" />
          </motion.div>
          <h1 className="text-3xl font-bold">Outils Human & Entity Design</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez notre suite d'outils conçus pour vous aider à comprendre, harmoniser et optimiser votre énergie
          </p>
        </div>

        <Card className="bg-white/80 backdrop-blur-sm p-8">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#54075B]">Nos outils HED</h2>
              <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
                Le Human & Entity Design propose une approche unique pour comprendre et optimiser votre énergie. 
                Nos outils sont conçus pour vous accompagner dans cette démarche, en vous offrant des solutions 
                adaptées à votre type énergétique et à vos besoins spécifiques.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tools.filter(tool => tool.visible).map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col h-full"
                >
                  <Card className="flex-1 hover:shadow-lg transition-all h-full">
                    <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500"></div>
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-white rounded-xl shadow-sm">
                          {tool.icon}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{tool.title}</h3>
                          <div className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Disponible
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-4 flex-grow">{tool.description}</p>
                      
                      <Button asChild className="self-start bg-[#54075B] hover:bg-[#54075B]/90 transition-all duration-300">
                        <Link to={tool.link}>
                          Accéder
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm p-8">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#54075B]">Prochainement</h2>
              <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
                Découvrez les outils que nous développons actuellement pour enrichir votre expérience HED.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcomingTools.map((tool, index) => (
                <Card key={index} className="p-6 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-xl shadow-sm">
                      {tool.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{tool.title}</h3>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {tool.status}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm">{tool.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-[#54075B] to-purple-700 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Prêt à découvrir nos outils?</h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Commencez dès maintenant avec notre Baromètre Énergétique et découvrez comment optimiser votre énergie au quotidien.
            </p>
            <Button variant="secondary" size="lg" asChild>
              <Link to="/barometre">
                Découvrir le Baromètre HED
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}